import {ControllerFlowAPI} from '@wix/yoshi-flow-editor'
import {Reservation, Status} from '@wix/ambassador-table-reservations-v1-reservation/types'
import {
  ApprovalMode,
  ReservationLocation,
} from '@wix/ambassador-table-reservations-v1-reservation-location/types'
import {Member} from '@wix/ambassador-members-v1-member/types'
import {PaymentMethod} from '@wix/ambassador-cashier-pay-v2-payment-method/types'
import {SiteStructure} from '@wix/native-components-infra/dist/src/types/types'
import {BreadcrumbsItem} from 'wix-ui-tpa/dist/cssVars/types/components'
import {ResolvedError} from '@wix/yoshi-flow-editor/error-handler'

import {reservationLocationsService} from '../../services/reservationLocationsService'
import {reservationsService} from '../../services/reservationsService'
import {goToNewReservation} from '../../utils/navigation'
import {noop} from '../../utils/helpers'
import {getRegionalSettings} from '../../utils/flowAPI'
import {RequestStatus, wrapRequest} from '../../utils/wrapRequest'
import {createStorageProvider} from '../../utils/storageContext'
import {getReservationLocationsMock} from '../../editor/editorMocks/getReservationLocationsMock'
import {
  getReservationDataQueryParamsMock,
  getReservationMock,
  reserveReservationMock,
} from '../../editor/editorMocks/getReservationMock'
import {
  validateReservationDataQueryParam,
  validateReservationIdQueryParam,
} from '../../utils/queryParams'
import {getCurrentMember} from '../../services/memberService'
import {ApprovalTextEditorState} from '../../utils/constants'
import {getBreadcrumbsConfig} from '../../utils/getBreadcrumbsConfig'
import {getPaymentMethods} from '../../services/cashierPayService'

import {reserveReservation, ReserveReservationApiError} from './actions/reserveReservation'
import {createReservation} from './actions/createReservation'
import {getReservation} from './actions/getReservation'

interface ReservationDetailsData {
  getReservationLocationsStatus: RequestStatus
  getReservationStatus: RequestStatus
  submitReservationStatus: RequestStatus
  deleteReservationStatus: RequestStatus
  apiRequestErrorDetails?: ResolvedError | ReserveReservationApiError
  regionalSettings?: string
  reservationIdQueryParam?: string
  reservationDataQueryParams?: {
    startDate?: string
    partySize?: string
    reservationLocationId?: string
  }
  isReservationDataQueryParamsValid: boolean
  isReservationIdQueryParamValid: boolean
  isManualApproval: boolean
  reservation?: Reservation
  reservationLocations: ReservationLocation[]
  fitToContentHeight: boolean
  currentMember?: Member
  getCurrentMemberStatus: RequestStatus
  paymentMethods?: PaymentMethod[] | undefined
  getPaymentMethodsStatus: RequestStatus
  approvalTextEditorState: ApprovalTextEditorState
  breadcrumbsConfig: BreadcrumbsItem[]
}

interface ReservationDetailsActions {
  getReservationLocations: () => Promise<ReservationLocation[] | undefined>
  getReservation: ReturnType<typeof getReservation>
  createReservation: ReturnType<typeof createReservation>
  reserveReservation: ReturnType<typeof reserveReservation>
  deleteReservation: (reservationId: string) => Promise<void>
  changeReservation: (reservation?: Reservation) => void
  goToNewReservation: (reservation?: Reservation) => void
  getCurrentMember: () => Promise<Member | null | undefined>
  getPaymentMethods: () => Promise<PaymentMethod[] | undefined>
}

export interface ReservationDetailsStorage
  extends ReservationDetailsData,
    ReservationDetailsActions {}

export const defaultReservationDetailsStorage: ReservationDetailsStorage = {
  getReservationLocationsStatus: RequestStatus.DEFAULT,
  getReservationStatus: RequestStatus.DEFAULT,
  submitReservationStatus: RequestStatus.DEFAULT,
  deleteReservationStatus: RequestStatus.DEFAULT,
  getCurrentMemberStatus: RequestStatus.DEFAULT,
  getPaymentMethodsStatus: RequestStatus.DEFAULT,
  reservationLocations: [],
  paymentMethods: [],
  getReservationLocations: noop,
  isReservationIdQueryParamValid: false,
  isReservationDataQueryParamsValid: false,
  isManualApproval: false,
  getReservation: noop,
  reserveReservation: noop,
  createReservation: noop,
  deleteReservation: noop,
  changeReservation: noop,
  goToNewReservation: noop,
  fitToContentHeight: true,
  getCurrentMember: noop,
  approvalTextEditorState: ApprovalTextEditorState.unknown,
  breadcrumbsConfig: [],
  getPaymentMethods: noop,
}

const {withStorageProvider, useStorage} = createStorageProvider(defaultReservationDetailsStorage)

const initReservationDetailsStorage = (
  flowAPI: ControllerFlowAPI,
  siteStructure: SiteStructure,
): ReservationDetailsStorage => {
  const query = flowAPI.controllerConfig?.wixCodeApi?.location?.query ?? {}

  const isReservationDataQueryParamsValid = validateReservationDataQueryParam(query)
  const isReservationIdQueryParamValid = validateReservationIdQueryParam(query)
  const isManualApproval = isReservationDataQueryParamsValid

  const storage: ReservationDetailsStorage = {
    ...defaultReservationDetailsStorage,
    isManualApproval,
    reservationIdQueryParam: query.reservationId,
    reservationDataQueryParams: {
      startDate: query.startDate,
      partySize: query.partySize,
      reservationLocationId: query.reservationLocationId,
    },
    isReservationDataQueryParamsValid,
    isReservationIdQueryParamValid,
    regionalSettings: getRegionalSettings(flowAPI),
    breadcrumbsConfig: getBreadcrumbsConfig(flowAPI, siteStructure),
    getReservationLocations: wrapRequest(
      flowAPI,
      reservationLocationsService.getReservationLocations,
      'reservationLocations',
      'getReservationLocationsStatus',
    ),
    getReservation: getReservation(flowAPI),
    reserveReservation: reserveReservation(flowAPI),
    createReservation: createReservation(flowAPI),
    deleteReservation: wrapRequest(
      flowAPI,
      reservationsService.deleteReservation,
      'reservation',
      'deleteReservationStatus',
    ),
    goToNewReservation: (reservation) =>
      goToNewReservation({flowAPI, reservationData: reservation?.details}),
    changeReservation: async (reservation) => {
      if (reservation?.id && reservation.revision) {
        try {
          await storage.deleteReservation(reservation.id)
        } catch (e) {}
      }
      await storage.goToNewReservation(reservation)
    },
    getCurrentMember: wrapRequest(
      flowAPI,
      getCurrentMember,
      'currentMember',
      'getCurrentMemberStatus',
    ),
    getPaymentMethods: wrapRequest(
      flowAPI,
      getPaymentMethods,
      'paymentMethods',
      'getPaymentMethodsStatus',
    ),
  }

  return storage
}

const mockReservationDetailsStorage = async (
  flowAPI: ControllerFlowAPI,
  siteStructure: SiteStructure,
): Promise<ReservationDetailsStorage> => {
  const reservationLocations = await getReservationLocationsMock(flowAPI)
  const reservation = getReservationMock(flowAPI, Status.HELD, reservationLocations[0].id!)
  const isManualApproval =
    reservationLocations[0].configuration?.onlineReservations?.approval?.mode ===
      ApprovalMode.MANUAL || false

  return {
    ...defaultReservationDetailsStorage,
    reservationLocations,
    regionalSettings: getRegionalSettings(flowAPI),
    getReservationLocationsStatus: RequestStatus.RESOLVED,
    getReservationStatus: RequestStatus.RESOLVED,
    reservationIdQueryParam: reservation.id!,
    isManualApproval,
    reservationDataQueryParams: getReservationDataQueryParamsMock(
      flowAPI,
      reservationLocations[0].id!,
    ),
    isReservationDataQueryParamsValid: false,
    isReservationIdQueryParamValid: true,
    reservation,
    breadcrumbsConfig: getBreadcrumbsConfig(flowAPI, siteStructure),
    goToNewReservation: () => {
      goToNewReservation({flowAPI})
    },
    changeReservation: () => {
      goToNewReservation({flowAPI})
    },
    reserveReservation: reserveReservationMock(flowAPI),
  }
}

export {
  initReservationDetailsStorage,
  mockReservationDetailsStorage,
  withStorageProvider as withReservationDetailsStorageProvider,
  useStorage as useReservationDetailsStorage,
}
